import React, { useMemo, useCallback, useState } from "react";

import { FormattedMessage } from "react-intl";

import useURLSearchState, { transforms } from "Hooks/useURLSearchState";
import HostSelector from "src/organization/pages/project/environment/metrics/components/HostSelector";
import TimeframeControl from "src/organization/pages/project/environment/metrics/components/timeframeControl/TimeframeControl";
import Cpu from "src/organization/pages/project/environment/metrics/containers/Cpu";
import Disk from "src/organization/pages/project/environment/metrics/containers/Disk";
import Memory from "src/organization/pages/project/environment/metrics/containers/Memory";
import useActivityToggle from "src/organization/pages/project/environment/metrics/hooks/useActivityToggle";
import useInitialTimeframe from "src/organization/pages/project/environment/metrics/hooks/useInitialTimeframe";
import { MetricsContext } from "src/organization/pages/project/environment/metrics/hooks/useMetricsContext";
import usePinnedTabsSync from "src/organization/pages/project/environment/metrics/hooks/usePinnedTabsSync";
import useStickyTimecontrol from "src/organization/pages/project/environment/metrics/hooks/useStickyTimecontrol";
import * as S from "src/organization/pages/project/environment/metrics/resources/enterprise/dedicated/styles";
import { DG2_QUERY } from "src/organization/pages/project/environment/metrics/resources/settings";
import { TimeFrameType } from "src/organization/pages/project/environment/metrics/resources/types";
import * as V from "src/organization/pages/project/environment/metrics/resources/views.styles";
import { getPollInterval } from "src/organization/pages/project/environment/metrics/utils/polling/utils";

import useEnterprise from "../useEnterprise/useEnterprise";

const getQuery = collection => ({
  fields: DG2_QUERY,
  stream: {
    collection,
    stream: "metrics"
  }
});

const Enterprise = ({ link }) => {
  const [isDeployment, toggleDeployment] = useActivityToggle();
  const initialTimeframe = useInitialTimeframe();
  const [timeFrame, setTimeFrame] = useState<TimeFrameType>(initialTimeframe);
  const pollInterval = getPollInterval(timeFrame);

  const query = useMemo(() => getQuery(link.collection), [link.collection]);

  const { data, status, lastUpdated } = useEnterprise({
    url: link.href,
    timeFrame,
    query
  });

  const hostList = data?.hosts;
  const mountpoints = data?.mountpoint;

  const [hosts, setHosts] = useURLSearchState(
    hostList,
    "hosts",
    transforms.array
  );

  const selectedHosts = useMemo(() => {
    return hosts?.map(host =>
      hostList?.find(fullHost => fullHost?.startsWith(host))
    );
  }, [hostList, hosts]);

  const onBrush = useCallback(
    timeFrame => {
      setTimeFrame(timeFrame);
    },
    [setTimeFrame]
  );

  const { onSyncChange, isSynced, canJoinSync } = usePinnedTabsSync({
    setTimeFrame: setTimeFrame,
    timeFrame: timeFrame
  });

  const handleSyncChange = (isSynced: boolean) => {
    onSyncChange(isSynced, timeFrame);
  };

  const isFetching = status.refresh;

  const stickyRef = useStickyTimecontrol();

  return (
    <MetricsContext.Provider value={{ timeFrame, isDeployment, setTimeFrame }}>
      <V.Header ref={stickyRef}>
        <V.Heading tag="h2">
          <FormattedMessage id="metrics.dedicated.heading" />
        </V.Heading>
        <TimeframeControl
          timeframe={{
            timeframe: timeFrame,
            onChange: setTimeFrame
          }}
          isUpdating={status.isLoading}
          pollInterval={pollInterval}
          lastUpdated={lastUpdated}
          tabSync={{
            onSyncChange: handleSyncChange,
            isSynced,
            canJoinSync
          }}
          deployment={{
            enabled: isDeployment,
            onChange: toggleDeployment
          }}
        >
          <HostSelector hosts={hostList} selected={hosts} onChange={setHosts} />
        </TimeframeControl>
      </V.Header>
      <S.Layout>
        <Cpu
          service={null}
          hosts={selectedHosts}
          data={data?.cpu}
          isLoading={isFetching}
          timeframe={timeFrame}
          onBrush={onBrush}
        />
        <Memory
          service={null}
          hosts={selectedHosts}
          data={data?.memory}
          isLoading={isFetching}
          timeframe={timeFrame}
          onBrush={onBrush}
        />
      </S.Layout>
      <S.DisksLayout>
        {mountpoints?.map(
          mountpoint =>
            data?.disks &&
            data?.disks[mountpoint] && (
              <Disk
                service={null}
                diskPerRow={mountpoints.length > 2 ? 3 : 2}
                key={mountpoint}
                hosts={selectedHosts}
                mountpoint={mountpoint}
                isLoading={isFetching}
                data={data.disks[mountpoint]}
                timeframe={timeFrame}
                onBrush={onBrush}
              />
            )
        )}
      </S.DisksLayout>
    </MetricsContext.Provider>
  );
};

export default Enterprise;
